import {updateDoc, addDoc, collection, doc} from "firebase/firestore";
import {Button, Col, Form, FormSelect, Modal, Row, Spinner} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faSave} from "@fortawesome/free-solid-svg-icons";
import {FormEvent, useEffect, useState} from "react";
import {
    removeUndefinedFields,
    showErrorsListInToast,
} from "../../tools";
import {RREmployeeType} from "../../types/RREmployeeType";
import {useAppSelector} from "../../hooks";
import {selectUsers} from "../../../features/AppAuthSlice";
import {AppUserType} from "../../types/AppUserType";
import {appFirestore} from "../../../index";
import {assertEmployeeEmailAvailable, normalizeEmployeeEmail} from "./employeeEmail";


export interface EmployeeAuthProps {
    employee: RREmployeeType;
    onDone: () => any;
}

export function EmployeeAuth({
                                 employee,
                                 onDone
                             }: EmployeeAuthProps) {

    const [saving, setSaving] = useState(false);
    const [editEmpl, setEditEmpl] = useState<RREmployeeType>(employee);
    const users = useAppSelector(selectUsers);

    useEffect(() => {
        setEditEmpl(employee);
    }, [employee]);

    const save = async (data: any) => {
        try {
            if(editEmpl.isSuperAdmin) {
                editEmpl.isAdmin = true;
            }

            await updateDoc(employee.ref!, {
                isSuperAdmin: editEmpl.isSuperAdmin || false,
                isAdmin: editEmpl.isAdmin || false,
                isEmployee: editEmpl.isEmployee || false,
                isCatering: editEmpl.isCatering || false,
                isMontage: editEmpl.isMontage || false,
                isStore: editEmpl.isStore || false,
                isLogistics: editEmpl.isLogistics || false,
                canSeePayment: editEmpl.canSeePayment || false,
                canSeeLogisticsScheduleReport: editEmpl.canSeeLogisticsScheduleReport || false,
                canUseMobileApp: editEmpl.canUseMobileApp || false,
                canUseWebApp: editEmpl.canUseWebApp || false,
            });

            setSaving(false);
            onDone();
        } catch (e: any) {
            showErrorsListInToast("Грешка", [e.message])
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) return;

        if (editEmpl.password && editEmpl.password.length < 6) {
            showErrorsListInToast("Грешка", ["Паролата е твърде кратка. Минималната дължина е 6 знака."]);
            return
        }

        setSaving(true);
        const user = users.find(
            u => u.employeeId === employee.id
        );
        const email = normalizeEmployeeEmail(employee.email || "");

        const newUserData = removeUndefinedFields({
            employeeId: employee.id,
            name: employee.name,
            phone: employee.phone || "",
            email,
            password: editEmpl.password,
            validFromDate: employee.startDate,
            validToDate: employee.leaveDate,
            validPeriod: {
                from: employee.startDate,
                to: employee.leaveDate
            },
            isLogistics: editEmpl.isLogistics || false,
            isMontage: editEmpl.isMontage || false,
            isCatering: editEmpl.isCatering || false,
            isStore: editEmpl.isStore || false,
        });

        try {
            await assertEmployeeEmailAvailable(email, employee.id);
            if (user) {
                await updateDoc(user.ref!, newUserData!);
            } else {
                if (newUserData?.password) {
                    await addDoc(collection(appFirestore, "users"), newUserData);
                }
            }
        } catch (error) {
            showErrorsListInToast("Грешка", [error instanceof Error ? error.message : "Неуспешно записване на правата."]);
            setSaving(false)
            return
        }
        save(editEmpl);
    }

    return (
        <Form className={""} onSubmit={handleSubmit}>
            <Modal.Body>
                <h5 className={"mb-3"}>
                    Парола и права на <strong>{editEmpl.name}</strong>
                </h5>

                <Row>
                    <Col xs={6}>
                        <Form.Group>
                            <Form.Label className={"fw-bold"}>Парола:</Form.Label>
                            <Form.Control type="password" name="name" value={editEmpl.password}
                                          placeholder={"Минимална дължина на паролата - 6 знака!"}
                                          onChange={event => setEditEmpl({...editEmpl, password: event.target.value})}
                            />
                            <Form.Text className="text-muted">
                                Оставете празно или попълнете за нова парола.
                            </Form.Text>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Label className={"fw-bold"}>Роли:</Form.Label>
                        <Form.Group className="mt-3" controlId="isSuperAdminCheckbox">
                            <Form.Check type="checkbox" label="Администратор"
                                        onChange={event => setEditEmpl({...editEmpl, isSuperAdmin: event.target.checked})}
                                        checked={editEmpl.isSuperAdmin}/>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Label className={"fw-bold"}>&nbsp;</Form.Label>
                        <Form.Group className="mt-3" controlId="isAdminCheckbox">
                            <Form.Check type="checkbox" label="Оператор"
                                        onChange={event => setEditEmpl({...editEmpl, isAdmin: event.target.checked})}
                                        checked={editEmpl.isAdmin}/>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Label>&nbsp;</Form.Label>
                        <Form.Group className="mt-3" controlId="isEmployeeCheckbox">
                            <Form.Check type="checkbox" label=" Служител"
                                        onChange={event => setEditEmpl({...editEmpl, isEmployee: event.target.checked})}
                                        checked={editEmpl.isEmployee}/>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className={"mt-3"}>
                    <Col>
                        <Form.Label className={"fw-bold"}>Отдели:</Form.Label>
                        <Form.Group className="mt-3" controlId="isMontageCheckbox">
                            <Form.Check type="checkbox" label=" Монтаж"
                                        onChange={event => setEditEmpl({...editEmpl, isMontage: event.target.checked})}
                                        checked={editEmpl.isMontage}/>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Label>&nbsp;</Form.Label>
                        <Form.Group className="mt-3" controlId="isCateringCheckbox">
                            <Form.Check type="checkbox" label=" Кетъринг"
                                        onChange={event => setEditEmpl({...editEmpl, isCatering: event.target.checked})}
                                        checked={editEmpl.isCatering}/>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Label>&nbsp;</Form.Label>
                        <Form.Group className="mt-3" controlId="isStoreCheckbox">
                            <Form.Check type="checkbox" label=" Кухня"
                                        onChange={event => setEditEmpl({...editEmpl, isStore: event.target.checked})}
                                        checked={editEmpl.isStore}/>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Label>&nbsp;</Form.Label>
                        <Form.Group className="mt-3" controlId="isLogisticsCheckbox">
                            <Form.Check type="checkbox" label=" Транспорт"
                                        onChange={event => setEditEmpl({
                                            ...editEmpl,
                                            isLogistics: event.target.checked
                                        })}
                                        checked={editEmpl.isLogistics}/>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Label className={"fw-bold"}>Права:</Form.Label>
                        <Form.Group className="mt-3" controlId="canSeePaymentCheckbox">
                            <Form.Check type="checkbox" label=" Да вижда прихода"
                                        onChange={event => setEditEmpl({
                                            ...editEmpl,
                                            canSeePayment: event.target.checked
                                        })}
                                        checked={editEmpl.canSeePayment}/>
                        </Form.Group>
                        <Form.Group className="mt-3" controlId="canSeeLogisticsScheduleReportCheckbox">
                            <Form.Check type="checkbox" label=" Справка „График“"
                                        onChange={event => setEditEmpl({
                                            ...editEmpl,
                                            canSeeLogisticsScheduleReport: event.target.checked
                                        })}
                                        checked={editEmpl.canSeeLogisticsScheduleReport}/>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className={"mt-3"}>
                    <Col xs={12}>
                        <b>Достъп до приложения:</b>
                    </Col>
                    <Col>
                        <Form.Group className="mt-3">
                            <Form.Check type="checkbox" label=" Мобилна версия"
                                        onChange={event => setEditEmpl({
                                            ...editEmpl,
                                            canUseMobileApp: event.target.checked
                                        })}
                                        checked={editEmpl.canUseMobileApp}/>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className={"mt-3"}>
                            <Form.Check type="checkbox" label=" Десктоп версия"
                                        onChange={event => setEditEmpl({
                                            ...editEmpl,
                                            canUseWebApp: event.target.checked
                                        })}
                                        checked={editEmpl.canUseWebApp}/>
                        </Form.Group>
                    </Col>
                    <Col />
                </Row>

            </Modal.Body>
            <Modal.Footer>
                <Row className={"w-100"}>
                    {
                        saving && <Col xs={12} className={"text-center"}>
                            <Spinner animation={"border"}/>
                        </Col>
                    }

                    {
                        !saving &&
                        <>
                            <Col xs={6}>
                                <Button variant={"outline-secondary"} onClick={() => {
                                    onDone()
                                }}>
                                    <FontAwesomeIcon icon={faArrowLeft}/> Към списъка
                                </Button>
                            </Col>
                            <Col xs={6} className={"text-end"}>
                                <Button type={"submit"}>
                                    <FontAwesomeIcon icon={faSave}/> Запиши
                                </Button>
                            </Col>
                        </>
                    }
                </Row>
            </Modal.Footer>
        </Form>
    )
}
